import type { Player, PlayerSportStats, Sport } from '@/types/api'
import { SportEnumValue } from '@/types/api'

/**
 * Resolve sport-specific statistics for a player regardless of how the backend serialized the keys.
 * Handles canonical enum names ("SPORT_TABLE_TENNIS"), numeric enum values ("1"),
 * and legacy lowercase representations ("table_tennis", "sport_table_tennis").
 */
export function resolvePlayerStatsBySport(player: Player, sport: Sport): PlayerSportStats | undefined {
  const statsBySport = player.statsBySport
  if (!statsBySport) {
    return undefined
  }

  // Direct enum name match (e.g., "SPORT_TABLE_TENNIS")
  const direct = statsBySport[sport]
  if (direct) {
    return direct
  }

  // Numeric enum value stored as string ("1")
  const numericKey = String(SportEnumValue[sport])
  if (numericKey in statsBySport) {
    return statsBySport[numericKey]
  }

  // Legacy lowercase or partial keys
  const normalizedVariants = new Set<string>()
  const baseName = sport.replace(/^SPORT_/, '')
  normalizedVariants.add(baseName.toLowerCase())
  normalizedVariants.add(`sport_${baseName.toLowerCase()}`)

  for (const [key, value] of Object.entries(statsBySport)) {
    const normalizedKey = key.toLowerCase()
    if (normalizedVariants.has(normalizedKey)) {
      return value
    }
  }

  return undefined
}
